import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Divider,
  Stack,
  Chip,
  Alert,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Switch,
  FormControlLabel,
} from "@mui/material";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";

import { API_URL } from "./config";

// ================== ✅ Theme constants ==================
const THEME_ORANGE = "#F07828";
const THEME_ORANGE_2 = "#ed7f35ff";
const BG = "#F2F2F2";
const THEME_BLUE = "#1976d2";

const BORDER = "rgba(25,118,210,0.20)";
const BORDER_HOVER = "rgba(25,118,210,0.45)";
const BORDER_FOCUS = "rgba(240,120,40,0.80)";
const ROW_ALT = "rgba(25,118,210,0.06)";
const ROW_HOVER = "rgba(25,118,210,0.10)";

const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };
const rootPaperSx = {
  mx: "auto",
  borderRadius: 2,
  overflow: "hidden",
  border: "1px solid rgba(25,118,210,0.15)",
};

const headerSx = {
  px: 2,
  py: 1.5,
  background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
  color: "white",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 2,
  flexWrap: "wrap",
};

const bodySx = { p: 2, backgroundColor: BG };

const surfaceSx = {
  borderRadius: 2,
  backgroundColor: "white",
  border: `1px solid ${BORDER}`,
};

const inputSx = {
  "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
};

const primaryBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  backgroundColor: THEME_ORANGE,
  "&:hover": { backgroundColor: THEME_ORANGE_2 },
};

const outlinedBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "rgba(25,118,210,0.35)",
  color: "rgba(25,118,210,0.95)",
  "&:hover": {
    borderColor: "rgba(25,118,210,0.55)",
    backgroundColor: "rgba(25,118,210,0.04)",
  },
};

const dangerOutlinedBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  borderColor: "rgba(240,120,40,0.55)",
  color: THEME_ORANGE,
  "&:hover": { borderColor: THEME_ORANGE, backgroundColor: "rgba(240,120,40,0.06)" },
};

const blueBtnSx = {
  borderRadius: 2,
  textTransform: "none",
  fontWeight: 900,
  backgroundColor: "rgba(25,118,210,0.95)",
  "&:hover": { backgroundColor: "rgba(25,118,210,0.85)" },
};

const ACH_TYPES = [
  { key: "extracurricular", label: "Extracurricular" },
  { key: "competitive_exam", label: "Competitive Exam" },
  { key: "hackathon", label: "Hackathon" },
  { key: "project", label: "Project" },
  { key: "certification", label: "Certification" },
  { key: "internship", label: "Internship" },
  { key: "workshop", label: "Workshop" },
  { key: "sports", label: "Sports" },
  { key: "volunteer", label: "Volunteer" },
  { key: "paper", label: "Paper / Presentation" },
];

// ================== ✅ Token helpers ==================
function getAuthToken() {
  const raw = localStorage.getItem("auth") || localStorage.getItem("token") || "";
  const cleaned = raw.startsWith("Bearer ") ? raw.slice(7) : raw;
  return cleaned.trim();
}
function isProbablyJwt(token) {
  if (!token) return false;
  const parts = token.split(".");
  return parts.length === 3 && parts.every((p) => p && p.length > 10);
}

// Small helper: safe execCommand (works in most browsers)
function execCmd(cmd, value = null) {
  try {
    document.execCommand(cmd, false, value);
  } catch (e) {
    // ignore
  }
}

export default function StudentCareerProfile({ studentKey: studentKeyProp }) {
  const token = getAuthToken();

  const authHeaders = () => {
    if (!isProbablyJwt(token)) return null;
    return { Authorization: `Bearer ${token}` };
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [interestAreas, setInterestAreas] = useState([]);
  const [studentInterests, setStudentInterests] = useState([]);
  const [skillsMaster, setSkillsMaster] = useState([]);
  const [achievements, setAchievements] = useState([]);

  // -------------------- Resume list + selection --------------------
  const [snapshots, setSnapshots] = useState([]); // list for dropdown
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(""); // ResumeSnapshotId
  const [selectedSnapshotMeta, setSelectedSnapshotMeta] = useState(null); // {TargetRole, CreatedAt}

  // Resume content (selected)
  const [targetRole, setTargetRole] = useState("Frontend Intern");
  const [resumeText, setResumeText] = useState("");
  const [resumeDirty, setResumeDirty] = useState(false);

  // One-page AI per TargetRole
  const [onePageOpen, setOnePageOpen] = useState(false);
  const [onePageLoading, setOnePageLoading] = useState(false);
  const [onePageText, setOnePageText] = useState(""); // stored HTML
  const [onePageMeta, setOnePageMeta] = useState(null);
  const [onePageDirty, setOnePageDirty] = useState(false);

  // Edit modes for AI dialog
  const [onePageMode, setOnePageMode] = useState("preview"); // "preview" | "visual" | "html"
  const editorRef = useRef(null);

  // Add achievement dialog
  const [openAdd, setOpenAdd] = useState(false);
  const [newAch, setNewAch] = useState({
    AchievementType: "hackathon",
    Title: "",
    OrgOrEvent: "",
    Role: "",
    Level: "college",
    Outcome: "",
    StartDate: "",
    EndDate: "",
    Description: "",
    ProofUrl: "",
    Skills: [],
  });

  const selectedInterestIds = useMemo(
    () => new Set(studentInterests.map((x) => x.InterestAreaId)),
    [studentInterests]
  );

  // -------------------- LOADERS --------------------
  async function loadSnapshotsList() {
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");

    try {
      const params = studentKeyProp ? { studentKey: studentKeyProp } : undefined;
      const resp = await axios.get(`${API_URL}/api/career/student/resume/snapshots`, {
        headers: h,
        params,
      });
      setSnapshots(resp.data || []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load resume list");
    }
  }

  async function loadSnapshotById(id, { force = false } = {}) {
  setError("");
  if (!id) return;

  const h = authHeaders();
  if (!h) return setError("Auth token missing/invalid. Please login again.");

  // ✅ Only block switching when NOT forced
  if (resumeDirty && !force) {
    return setError("You have unsaved changes in Resume Text. Save/discard before switching resumes.");
  }

  try {
    const resp = await axios.get(`${API_URL}/api/career/student/resume/snapshots/${id}`, { headers: h });

    setResumeText(resp.data?.Text || "");
    setTargetRole(resp.data?.TargetRole || "");
    setSelectedSnapshotMeta({
      CreatedAt: resp.data?.CreatedAt,
      TargetRole: resp.data?.TargetRole,
    });

    // ✅ once loaded from DB, it’s not dirty
    setResumeDirty(false);
  } catch (e) {
    console.error(e);
    setError(e?.response?.data?.error || "Failed to load snapshot");
  }
}


  async function loadAll() {
    setError("");
    const h = authHeaders();
    if (!h) {
      setError("Auth token missing/invalid (jwt malformed). Please login again.");
      return;
    }

    setLoading(true);
    try {
      const params = studentKeyProp ? { studentKey: studentKeyProp } : undefined;

      const [ia, si, sk, ach] = await Promise.all([
        axios.get(`${API_URL}/api/career/interest-areas`, { headers: h }),
        axios.get(`${API_URL}/api/career/student/interests`, { headers: h, params }),
        axios.get(`${API_URL}/api/career/skills`, { headers: h }),
        axios.get(`${API_URL}/api/career/student/achievements`, { headers: h, params }),
      ]);

      setInterestAreas(ia.data || []);
      setSkillsMaster(sk.data || []);
      setAchievements(ach.data || []);

      const siRows = si.data || [];
      setStudentInterests(
        siRows.map((x) => ({
          InterestAreaId: x.InterestAreaId,
          InterestStrength: x.InterestStrength || 3,
          InterestLabel: x.InterestLabel,
        }))
      );

      // ✅ also load resume dropdown list
      await loadSnapshotsList();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load career profile");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentKeyProp]);

  // -------------------- Interests (unchanged) --------------------
  async function saveInterests() {
    setError("");
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");

    try {
      const payload = {
        studentKey: studentKeyProp || undefined,
        interests: studentInterests.map((x) => ({
          InterestAreaId: x.InterestAreaId,
          InterestStrength: x.InterestStrength,
        })),
      };
      await axios.post(`${API_URL}/api/career/student/interests`, payload, { headers: h });
      await loadAll();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to save interests");
    }
  }

  async function inferInterests() {
    setError("");
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");

    try {
      await axios.post(
        `${API_URL}/api/career/student/interests/infer`,
        { studentKey: studentKeyProp || undefined },
        { headers: h }
      );
      await loadAll();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to infer interests");
    }
  }

  // -------------------- Achievements (unchanged) --------------------
  async function createAchievement() {
    setError("");
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");

    try {
      const payload = {
        studentKey: studentKeyProp || undefined,
        ...newAch,
        Skills: (newAch.Skills || []).map((s) => ({
          SkillId: s.SkillId,
          ConfidenceScore: s.ConfidenceScore || 3,
        })),
      };
      await axios.post(`${API_URL}/api/career/student/achievements`, payload, { headers: h });

      setOpenAdd(false);
      setNewAch({
        AchievementType: "hackathon",
        Title: "",
        OrgOrEvent: "",
        Role: "",
        Level: "college",
        Outcome: "",
        StartDate: "",
        EndDate: "",
        Description: "",
        ProofUrl: "",
        Skills: [],
      });

      await loadAll();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to create achievement");
    }
  }

  async function deleteAchievement(id) {
    setError("");
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");

    try {
      await axios.delete(`${API_URL}/api/career/student/achievements/${id}`, { headers: h });
      await loadAll();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to delete achievement");
    }
  }

  // -------------------- Resume generation (existing) --------------------
  async function generateResume() {
    setError("");

    if (resumeDirty) {
      return setError("You have unsaved changes in Resume Text. Save/discard before generating again.");
    }

    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");

    try {
      const payload = { studentKey: studentKeyProp || undefined, TargetRole: targetRole };
      const resp = await axios.post(`${API_URL}/api/career/student/resume/generate`, payload, { headers: h });

      setResumeText(resp.data?.text || "");
      setResumeDirty(false);

      // after generating, refresh dropdown list so new/updated appears
      await loadSnapshotsList();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to generate resume");
    }
  }

  // -------------------- AI: view existing + generate new --------------------
  async function viewExistingAI() {
    setError("");
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");
    if (!targetRole?.trim()) return setError("TargetRole missing.");
    if (!selectedSnapshotId) return setError("Please select a resume from dropdown first.");

    if (onePageDirty) {
      return setError("You have unsaved changes in AI Resume. Save/discard before loading.");
    }

    try {
      const params = {
        TargetRole: targetRole,
        ...(studentKeyProp ? { studentKey: studentKeyProp } : {}),
      };

      const resp = await axios.get(`${API_URL}/api/aiResume/one-page-resume/latest`, {
        headers: h,
        params,
      });

      if (resp.data?.exists === false) {
        setOnePageText("");
        setOnePageMeta(null);
        setOnePageDirty(false);
        setOnePageMode("preview");
        setOnePageOpen(true);
        setError("No AI resume saved yet for this TargetRole. Click 'Generate New AI Resume'.");
        return;
      }

      setOnePageText(resp.data?.text || "");
      setOnePageMeta(resp.data?.meta || null);
      setOnePageDirty(false);
      setOnePageMode("preview");
      setOnePageOpen(true);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load existing AI resume");
    }
  }

  async function generateNewAI() {
    setError("");
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");
    if (!selectedSnapshotId) return setError("Please select a resume from dropdown first.");

    if (!resumeText || resumeText.trim().length < 30) {
      return setError("Resume text is empty/too short. Load or generate a resume first.");
    }

    setOnePageLoading(true);
    try {
      const resp = await axios.post(
        `${API_URL}/api/aiResume/one-page-resume/generate`,
        {
          studentKey: studentKeyProp || undefined,
          TargetRole: targetRole,
          resumeText,
        },
        { headers: h }
      );

      setOnePageText(resp.data?.text || "");
      setOnePageMeta(resp.data?.meta || null);
      setOnePageDirty(false);
      setOnePageMode("preview");
      setOnePageOpen(true);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to generate new AI resume");
    } finally {
      setOnePageLoading(false);
    }
  }

  async function saveOnePageResume() {
    setError("");
    const h = authHeaders();
    if (!h) return setError("Auth token missing/invalid. Please login again.");
    if (!selectedSnapshotId) return setError("Please select a resume from dropdown first.");
    if (!targetRole?.trim()) return setError("TargetRole is required.");
    if (!onePageText?.trim()) return setError("AI resume is empty.");

    try {
      await axios.put(
        `${API_URL}/api/aiResume/one-page-resume`,
        {
          studentKey: studentKeyProp || undefined,
          TargetRole: targetRole,
          text: onePageText,
        },
        { headers: h }
      );

      setOnePageDirty(false);
      // refresh meta by reloading latest
      await viewExistingAI();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to save AI resume");
    }
  }

  async function copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text || "");
    } catch (e) {
      console.error("Clipboard copy failed", e);
      setError("Copy failed. Please select and copy manually.");
    }
  }

  async function saveSelectedSnapshotText() {
  setError("");
  const h = authHeaders();
  if (!h) return setError("Auth token missing/invalid. Please login again.");
  if (!selectedSnapshotId) return setError("Please select a resume first.");
  if (!resumeText?.trim()) return setError("Resume text is empty.");

  try {
    await axios.put(
      `${API_URL}/api/career/student/resume/snapshots/${selectedSnapshotId}`,
      { Text: resumeText },
      { headers: h }
    );

    setResumeDirty(false);
    await loadSnapshotsList(); // refresh dropdown label timestamps if you show them
  } catch (e) {
    console.error(e);
    setError(e?.response?.data?.error || "Failed to save resume snapshot");
  }
}

async function discardSnapshotEdits() {
  setError("");
  if (!selectedSnapshotId) return setError("Please select a resume first.");
  await loadSnapshotById(selectedSnapshotId, { force: true });
  setResumeDirty(false);
}

async function deleteSelectedSnapshot() {
  setError("");
  const h = authHeaders();
  if (!h) return setError("Auth token missing/invalid. Please login again.");
  if (!selectedSnapshotId) return setError("Please select a resume first.");

  const ok = window.confirm("Delete this resume snapshot permanently?");
  if (!ok) return;

  try {
    await axios.delete(`${API_URL}/api/career/student/resume/snapshots/${selectedSnapshotId}`, {
      headers: h,
    });

    // clear UI selection
    setSelectedSnapshotId("");
    setSelectedSnapshotMeta(null);
    setResumeText("");
    setTargetRole("Frontend Intern");
    setResumeDirty(false);

    await loadSnapshotsList();
  } catch (e) {
    console.error(e);
    setError(e?.response?.data?.error || "Failed to delete resume snapshot");
  }
}


  // When switching to visual mode, ensure editor contains HTML
  useEffect(() => {
    if (onePageOpen && onePageMode === "visual" && editorRef.current) {
      editorRef.current.innerHTML = onePageText || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onePageOpen, onePageMode]);

  // -------------------- UI --------------------
  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Career Profile (Living Resume)
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Add achievements + tag skills → resume updates automatically
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} disabled={loading} onClick={loadAll} title="Reload">
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {/* Interests */}
          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Interests</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              Choose interest areas (Frontend/Backend/AI/etc.)
            </Typography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Autocomplete
                  multiple
                  options={interestAreas}
                  getOptionLabel={(o) => o.InterestLabel || ""}
                  value={interestAreas.filter((i) => selectedInterestIds.has(i.InterestAreaId))}
                  onChange={(e, newVals) => {
                    setStudentInterests(
                      newVals.map((v) => {
                        const existing = studentInterests.find((x) => x.InterestAreaId === v.InterestAreaId);
                        return {
                          InterestAreaId: v.InterestAreaId,
                          InterestStrength: existing?.InterestStrength || 3,
                          InterestLabel: v.InterestLabel,
                        };
                      })
                    );
                  }}
                  renderInput={(params) => <TextField {...params} label="Select Interests" sx={inputSx} />}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <Button fullWidth variant="contained" onClick={saveInterests} disabled={loading} sx={primaryBtnSx}>
                  Save Interests
                </Button>
              </Grid>

              <Grid item xs={12} md={2}>
                <Button fullWidth variant="outlined" onClick={inferInterests} disabled={loading} sx={outlinedBtnSx}>
                  Infer
                </Button>
              </Grid>
            </Grid>

            {studentInterests.length > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                {studentInterests.map((x) => (
                  <Chip
                    key={x.InterestAreaId}
                    label={`${x.InterestLabel} (Strength: ${x.InterestStrength})`}
                    sx={{
                      mb: 1,
                      backgroundColor: ROW_ALT,
                      border: `1px solid ${BORDER}`,
                      fontWeight: 700,
                    }}
                  />
                ))}
              </Stack>
            )}
          </Paper>

          {/* Achievements */}
          <Paper elevation={0} sx={{ ...surfaceSx, p: 2, mb: 2 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Achievements Timeline</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Add hackathons, exams, projects, certifications, internships, etc.
                </Typography>
              </Box>

              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAdd(true)} sx={blueBtnSx}>
                Add Achievement
              </Button>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack spacing={1.2}>
              {achievements.length === 0 ? (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  No achievements yet.
                </Alert>
              ) : (
                achievements.map((a, idx) => (
                  <Paper
                    key={a.AchievementId}
                    elevation={0}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: `1px solid ${BORDER}`,
                      backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
                      "&:hover": { backgroundColor: ROW_HOVER },
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: 6,
                        background: `linear-gradient(180deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
                      }}
                    />

                    <Box sx={{ pl: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, alignItems: "flex-start" }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>{a.Title}</Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>
                            {a.AchievementType} • {a.OrgOrEvent || "—"} • {a.Level || "—"} • {a.Outcome || "—"}
                          </Typography>

                          {(a.StartDate || a.EndDate) && (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {a.StartDate ? dayjs(a.StartDate).format("DD MMM YYYY") : ""}
                              {a.EndDate ? ` to ${dayjs(a.EndDate).format("DD MMM YYYY")}` : ""}
                            </Typography>
                          )}
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => deleteAchievement(a.AchievementId)}
                          sx={dangerOutlinedBtnSx}
                        >
                          Delete
                        </Button>
                      </Box>

                      {a.Description && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {a.Description}
                        </Typography>
                      )}

                      {Array.isArray(a.Skills) && a.Skills.length > 0 && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                          {a.Skills.map((s) => (
                            <Chip
                              key={`${s.SkillId || s.SkillName}-${s.SkillCategory}`}
                              label={`${s.SkillName} (${s.SkillCategory})`}
                              size="small"
                              sx={{
                                mb: 1,
                                backgroundColor: "rgba(25,118,210,0.06)",
                                border: "1px solid rgba(25,118,210,0.20)",
                                fontWeight: 700,
                              }}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Paper>
                ))
              )}
            </Stack>
          </Paper>

          {/* Resume section with DROPDOWN */}
          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>Resume</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              Select a saved resume → then View AI / Generate New AI.
            </Typography>

            <Grid container spacing={2} alignItems="center">
              {/* ✅ Dropdown */}
              <Grid item xs={12} md={5}>
                <FormControl fullWidth>
                  <InputLabel>Load Resume</InputLabel>
                  <Select
                      label="Load Resume"
                      value={selectedSnapshotId}
                      onChange={async (e) => {
                        const nextId = e.target.value;
                    
                        // If unsaved, ask user
                        if (resumeDirty) {
                          const ok = window.confirm("You have unsaved changes. Discard changes and switch?");
                          if (!ok) return; // keep current selection
                          // discard + force load
                          setSelectedSnapshotId(nextId);
                          await loadSnapshotById(nextId, { force: true });
                          return;
                        }
                    
                        setSelectedSnapshotId(nextId);
                        await loadSnapshotById(nextId);
                      }}
                      sx={inputSx}
                  >
                    <MenuItem value="">—</MenuItem>
                    {snapshots.map((s) => (
                      <MenuItem key={s.ResumeSnapshotId} value={s.ResumeSnapshotId}>
                        {s.TargetRole} — {dayjs(s.CreatedAt).format("DD MMM YYYY, hh:mm A")}
                      </MenuItem>
                    ))}
                  </Select>

                  

                </FormControl>
              </Grid>

              {/* target role (auto from selected snapshot, still editable if you want) */}
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Target Role"
                  value={targetRole}
                  onChange={(e) => setTargetRole(e.target.value)}
                  sx={inputSx}
                />
              </Grid>

              {/* Actions */}
              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={generateResume}
                  startIcon={<AutoAwesomeIcon />}
                  sx={primaryBtnSx}
                  disabled={loading}
                  title="Generate/Update snapshot (your existing endpoint)"
                >
                  Generate Snapshot
                </Button>
              </Grid>
            </Grid>

            {/* ✅ Show AI buttons only after selecting a resume */}
            {selectedSnapshotId ? (
              <Paper
                elevation={0}
                sx={{
                  mt: 2,
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${BORDER}`,
                  backgroundColor: "white",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                  <Box>
                    <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                      AI Resume (for selected resume)
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Selected: {selectedSnapshotMeta?.TargetRole || targetRole}{" "}
                      {selectedSnapshotMeta?.CreatedAt ? `• Snapshot: ${dayjs(selectedSnapshotMeta.CreatedAt).format("DD MMM YYYY, hh:mm A")}` : ""}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                    <Button
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={viewExistingAI}
                      sx={outlinedBtnSx}
                      disabled={onePageLoading}
                      title="Loads already saved AI resume (if exists) for this TargetRole"
                    >
                      View AI Resume
                    </Button>

                    <Button
                      variant="contained"
                      startIcon={<AutoFixHighIcon />}
                      onClick={generateNewAI}
                      sx={primaryBtnSx}
                      disabled={onePageLoading}
                      title="Generates new AI resume from the current ResumeText"
                    >
                      {onePageLoading ? "Generating..." : "Generate New AI Resume"}
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            ) : (
              <Alert sx={{ mt: 2, borderRadius: 2 }} severity="info">
                Select a resume from dropdown to enable AI actions.
              </Alert>
            )}

            {/* Resume Text area */}
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${BORDER}`,
                backgroundColor: "white",
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Box>
                  <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                    Resume Text (Snapshot)
                  </Typography>
                  {!!selectedSnapshotMeta?.CreatedAt && (
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Snapshot created: {dayjs(selectedSnapshotMeta.CreatedAt).format("DD MMM YYYY, hh:mm A")}
                    </Typography>
                  )}
                </Box>
              
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
                  <Button
                    variant="outlined"
                    sx={outlinedBtnSx}
                    disabled={!resumeDirty || !selectedSnapshotId}
                    onClick={discardSnapshotEdits}
                    title="Discard local edits and reload from DB"
                  >
                    Discard
                  </Button>
              
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    sx={primaryBtnSx}
                    disabled={!resumeDirty || !selectedSnapshotId}
                    onClick={saveSelectedSnapshotText}
                    title="Save snapshot text into DB"
                  >
                    Save
                  </Button>
              
                  <Button
                    variant="outlined"
                    startIcon={<DeleteOutlineIcon />}
                    sx={dangerOutlinedBtnSx}
                    disabled={!selectedSnapshotId}
                    onClick={deleteSelectedSnapshot}
                    title="Delete this resume snapshot"
                  >
                    Delete
                  </Button>
                </Stack>
              </Box>
              
              <TextField
                fullWidth
                multiline
                minRows={10}
                value={resumeText}
                onChange={(e) => {
                  setResumeText(e.target.value);
                  setResumeDirty(true);
                }}
                placeholder="Select a resume or generate snapshot..."
                sx={{ ...inputSx, mt: 1 }}
              />

              {resumeDirty && (
                <Alert sx={{ mt: 1, borderRadius: 2 }} severity="warning">
                  You have unsaved changes. Click <b>Save</b> or <b>Discard</b>.
                </Alert>
              )}
            </Paper>

            {/*
            <Paper
              elevation={0}
              sx={{
                mt: 2,
                p: 1.5,
                borderRadius: 2,
                border: `1px solid ${BORDER}`,
                backgroundColor: "white",
              }}
            >
              <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mb: 1 }}>
                Resume Text (Snapshot)
              </Typography>

              <TextField
                fullWidth
                multiline
                minRows={10}
                value={resumeText}
                onChange={(e) => {
                  setResumeText(e.target.value);
                  setResumeDirty(true);
                }}
                placeholder="Select a resume or generate snapshot..."
                sx={{ ...inputSx, mt: 1 }}
              />

              {resumeDirty && (
                <Alert sx={{ mt: 1, borderRadius: 2 }} severity="warning">
                  You edited the snapshot text (not saved by any API in this screen).
                </Alert>
              )}
            </Paper>
            */}

            {/* One-Page Resume Dialog */}
            <Dialog open={onePageOpen} onClose={() => setOnePageOpen(false)} maxWidth="md" fullWidth>
              <DialogTitle
                sx={{
                  fontWeight: 900,
                  color: "white",
                  background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <AutoFixHighIcon />
                  One-Page Resume (AI)
                </Box>

                <IconButton onClick={() => setOnePageOpen(false)} sx={{ color: "white" }}>
                  <CloseIcon />
                </IconButton>
              </DialogTitle>

              <DialogContent dividers sx={{ backgroundColor: BG }}>
                <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                    <Box>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Edit without seeing HTML tags → use <b>Visual Edit</b>.
                      </Typography>
                      {onePageMeta?.GeneratedAt && (
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          Last AI save: {dayjs(onePageMeta.GeneratedAt).format("DD MMM YYYY, hh:mm A")}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
                      <Button variant="outlined" sx={outlinedBtnSx} onClick={viewExistingAI} startIcon={<RefreshIcon />}>
                        Reload
                      </Button>

                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Mode</InputLabel>
                        <Select
                          label="Mode"
                          value={onePageMode}
                          onChange={(e) => setOnePageMode(e.target.value)}
                          sx={inputSx}
                        >
                          <MenuItem value="preview">Preview</MenuItem>
                          <MenuItem value="visual">Visual Edit</MenuItem>
                          <MenuItem value="html">HTML Edit</MenuItem>
                        </Select>
                      </FormControl>
                    </Stack>
                  </Box>

                  <Divider sx={{ my: 1.5 }} />

                  {/* PREVIEW */}
                  {onePageMode === "preview" && (
                    <Paper
                      elevation={0}
                      sx={{
                        borderRadius: 2,
                        border: `1px solid ${BORDER}`,
                        backgroundColor: "white",
                        p: 2,
                        overflow: "auto",
                        maxHeight: "70vh",
                      }}
                    >
                      <Box
                        sx={{
                          fontFamily: `"Inter","Roboto","Helvetica","Arial",sans-serif`,
                          "& h1": { fontSize: "1.35rem", fontWeight: 900, margin: 0 },
                          "& h2": {
                            fontSize: "1.0rem",
                            fontWeight: 900,
                            margin: "14px 0 6px",
                            color: "rgba(25,118,210,0.95)",
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          },
                          "& p": { margin: "6px 0", lineHeight: 1.45, fontSize: "0.92rem" },
                          "& ul": { margin: "6px 0 0 18px", padding: 0 },
                          "& li": { marginBottom: "8px", fontSize: "0.92rem", lineHeight: 1.45 },
                          "& hr": { border: 0, borderTop: "1px solid rgba(25,118,210,0.18)", margin: "12px 0" },
                          "& strong": { fontWeight: 900 },
                        }}
                        dangerouslySetInnerHTML={{ __html: onePageText || "" }}
                      />
                    </Paper>
                  )}

                  {/* VISUAL EDIT (no tags) */}
                  {onePageMode === "visual" && (
                    <>
                      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap" }}>
                        <Button variant="outlined" sx={outlinedBtnSx} onClick={() => execCmd("bold")}>
                          Bold
                        </Button>
                        <Button variant="outlined" sx={outlinedBtnSx} onClick={() => execCmd("italic")}>
                          Italic
                        </Button>
                        <Button variant="outlined" sx={outlinedBtnSx} onClick={() => execCmd("insertUnorderedList")}>
                          Bullet List
                        </Button>
                        <Button variant="outlined" sx={outlinedBtnSx} onClick={() => execCmd("formatBlock", "h2")}>
                          Section Title (H2)
                        </Button>
                        <Button variant="outlined" sx={outlinedBtnSx} onClick={() => execCmd("formatBlock", "p")}>
                          Paragraph
                        </Button>
                      </Stack>

                      <Paper
                        elevation={0}
                        sx={{
                          borderRadius: 2,
                          border: `1px solid ${BORDER}`,
                          backgroundColor: "white",
                          p: 2,
                          overflow: "auto",
                          maxHeight: "70vh",
                        }}
                      >
                        <Box
                          ref={editorRef}
                          contentEditable
                          suppressContentEditableWarning
                          onInput={() => {
                            const html = editorRef.current?.innerHTML || "";
                            setOnePageText(html);
                            setOnePageDirty(true);
                          }}
                          sx={{
                            outline: "none",
                            fontFamily: `"Inter","Roboto","Helvetica","Arial",sans-serif`,
                            "& h1": { fontSize: "1.35rem", fontWeight: 900, margin: 0 },
                            "& h2": {
                              fontSize: "1.0rem",
                              fontWeight: 900,
                              margin: "14px 0 6px",
                              color: "rgba(25,118,210,0.95)",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            },
                            "& p": { margin: "6px 0", lineHeight: 1.45, fontSize: "0.92rem" },
                            "& ul": { margin: "6px 0 0 18px", padding: 0 },
                            "& li": { marginBottom: "8px", fontSize: "0.92rem", lineHeight: 1.45 },
                            "& hr": { border: 0, borderTop: "1px solid rgba(25,118,210,0.18)", margin: "12px 0" },
                            "& strong": { fontWeight: 900 },
                          }}
                          dangerouslySetInnerHTML={{ __html: onePageText || "" }}
                        />
                      </Paper>

                      {onePageDirty && (
                        <Alert sx={{ mt: 1, borderRadius: 2 }} severity="warning">
                          You have unsaved changes (Visual Edit).
                        </Alert>
                      )}
                    </>
                  )}

                  {/* HTML EDIT (shows tags) */}
                  {onePageMode === "html" && (
                    <>
                      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)", mb: 1 }}>
                        HTML Edit (advanced)
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        minRows={14}
                        value={onePageText}
                        onChange={(e) => {
                          setOnePageText(e.target.value);
                          setOnePageDirty(true);
                        }}
                        sx={inputSx}
                      />
                      {onePageDirty && (
                        <Alert sx={{ mt: 1, borderRadius: 2 }} severity="warning">
                          You have unsaved changes (HTML Edit).
                        </Alert>
                      )}
                    </>
                  )}
                </Paper>
              </DialogContent>

              <DialogActions sx={{ backgroundColor: BG, px: 2, py: 1.5 }}>
                <Button
                  variant="outlined"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => copyToClipboard(onePageText)}
                  sx={outlinedBtnSx}
                  disabled={!onePageText}
                >
                  Copy HTML
                </Button>

                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={saveOnePageResume}
                  sx={primaryBtnSx}
                  disabled={!onePageDirty}
                  title="Save edited AI resume"
                >
                  Save AI Resume
                </Button>

                <Button onClick={() => setOnePageOpen(false)} sx={outlinedBtnSx}>
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Paper>

          {/* Add Achievement Dialog (unchanged UI) */}
          <Dialog open={openAdd} onClose={() => setOpenAdd(false)} maxWidth="md" fullWidth>
            <DialogTitle
              sx={{
                fontWeight: 900,
                color: "white",
                background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
              }}
            >
              Add Achievement
            </DialogTitle>

            <DialogContent dividers sx={{ backgroundColor: BG }}>
              <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <FormControl fullWidth>
                      <InputLabel>Type</InputLabel>
                      <Select
                        label="Type"
                        value={newAch.AchievementType}
                        onChange={(e) => setNewAch((prev) => ({ ...prev, AchievementType: e.target.value }))}
                        sx={inputSx}
                      >
                        {ACH_TYPES.map((t) => (
                          <MenuItem key={t.key} value={t.key}>
                            {t.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <TextField
                      fullWidth
                      label="Title"
                      value={newAch.Title}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, Title: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Event / Organization"
                      value={newAch.OrgOrEvent}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, OrgOrEvent: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Role (Team Lead / Participant / etc.)"
                      value={newAch.Role}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, Role: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Level (college/state/national)"
                      value={newAch.Level}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, Level: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Outcome (Winner/Rank/Score)"
                      value={newAch.Outcome}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, Outcome: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <TextField
                      fullWidth
                      label="Proof URL (certificate/github)"
                      value={newAch.ProofUrl}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, ProofUrl: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Start Date (YYYY-MM-DD)"
                      value={newAch.StartDate}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, StartDate: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="End Date (YYYY-MM-DD)"
                      value={newAch.EndDate}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, EndDate: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Description"
                      value={newAch.Description}
                      onChange={(e) => setNewAch((prev) => ({ ...prev, Description: e.target.value }))}
                      sx={inputSx}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                      multiple
                      options={skillsMaster}
                      getOptionLabel={(o) => `${o.SkillName} (${o.SkillCategory})`}
                      value={newAch.Skills}
                      onChange={(e, newVals) => {
                        setNewAch((prev) => ({
                          ...prev,
                          Skills: newVals.map((v) => {
                            const ex = prev.Skills.find((s) => s.SkillId === v.SkillId);
                            return { ...v, ConfidenceScore: ex?.ConfidenceScore || 3 };
                          }),
                        }));
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Tag Skills (React, Node, SQL, etc.)" sx={inputSx} />
                      )}
                    />

                    {newAch.Skills.length > 0 && (
                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                        {newAch.Skills.map((s) => (
                          <Chip
                            key={s.SkillId}
                            label={`${s.SkillName} (${s.SkillCategory})`}
                            sx={{
                              mb: 1,
                              backgroundColor: "rgba(240,120,40,0.08)",
                              border: "1px solid rgba(240,120,40,0.25)",
                              fontWeight: 800,
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                  </Grid>
                </Grid>
              </Paper>
            </DialogContent>

            <DialogActions sx={{ backgroundColor: BG, px: 2, py: 1.5 }}>
              <Button onClick={() => setOpenAdd(false)} sx={outlinedBtnSx}>
                Cancel
              </Button>
              <Button variant="contained" onClick={createAchievement} sx={primaryBtnSx}>
                Save
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Paper>
    </Box>
  );
}
